import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Users, DollarSign, TrendingUp, Gift, Instagram, Youtube, Twitter, 
  Globe, ArrowLeft, CheckCircle, Clock, XCircle, Copy, ExternalLink,
  Sparkles, Target, Award
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CollaboratorsPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [collaboratorStatus, setCollaboratorStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [applying, setApplying] = useState(false);
  
  // Application form
  const [formData, setFormData] = useState({
    display_name: '',
    bio: '',
    follower_count: '',
    payment_email: '',
    payment_method: 'paypal',
    social_links: {
      instagram: '',
      youtube: '',
      twitter: '',
      website: '',
    }
  });

  useEffect(() => {
    fetchTiers();
    if (token) {
      fetchCollaboratorStatus();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchTiers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/discounts/collaborators/tiers`);
      const data = await res.json();
      setTiers(data.tiers || []);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
    }
  };

  const fetchCollaboratorStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/discounts/collaborators/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCollaboratorStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!formData.display_name || !formData.follower_count || !formData.payment_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setApplying(true);
    try {
      const res = await fetch(`${API_URL}/api/discounts/collaborators/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          display_name: formData.display_name,
          bio: formData.bio,
          follower_count: parseInt(formData.follower_count),
          payment_email: formData.payment_email,
          payment_method: formData.payment_method,
          social_links: formData.social_links,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setShowApplyDialog(false);
        fetchCollaboratorStatus();
      } else {
        toast.error(data.detail || 'Failed to submit application');
      }
    } catch (error) {
      toast.error('Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500"><Clock className="w-3 h-3 mr-1" /> Pending Review</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      case 'suspended':
        return <Badge className="bg-gray-500">Suspended</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  // If user is an approved collaborator, show their dashboard
  if (collaboratorStatus?.is_collaborator && collaboratorStatus?.collaborator?.status === 'approved') {
    const collab = collaboratorStatus.collaborator;
    const tierInfo = collaboratorStatus.tier_info;
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Button variant="ghost" onClick={() => {
            if (window.history.length > 2 && document.referrer) {
              navigate(-1);
            } else {
              navigate('/dashboard');
            }
          }} className="mb-6" data-testid="collaborator-dashboard-back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Collaborator Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Welcome back, {collab.display_name}!
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium opacity-90">Your Code</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold">{collab.personal_code}</span>
                  <Button size="sm" variant="ghost" className="text-white hover:bg-white/20" onClick={() => copyCode(collab.personal_code)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-sm opacity-75 mt-1">{collab.discount_percentage}% off for followers</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Total Referrals</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="text-2xl font-bold">{collab.total_referrals}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Total Earnings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  <span className="text-2xl font-bold">${collab.total_earnings.toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Pending Payout</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                  <span className="text-2xl font-bold">${collab.pending_earnings.toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tier Info */}
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-500" />
                    {collab.tier === 'pro' ? 'Pro Collaborator' : 'Starter Collaborator'}
                  </CardTitle>
                  <CardDescription>{tierInfo?.description}</CardDescription>
                </div>
                <Badge className={collab.tier === 'pro' ? 'bg-amber-500' : 'bg-blue-500'}>
                  {tierInfo?.commission}% Commission
                </Badge>
              </div>
            </CardHeader>
          </Card>

          {/* Recent Referrals */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Referrals</CardTitle>
              <CardDescription>Users who signed up with your code</CardDescription>
            </CardHeader>
            <CardContent>
              {collaboratorStatus.referrals?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No referrals yet. Share your code to start earning!</p>
              ) : (
                <div className="space-y-3">
                  {collaboratorStatus.referrals?.map((ref) => (
                    <div key={ref.referral_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <p className="font-medium">{ref.purchase_type === 'subscription' ? `${ref.purchase_tier} Plan` : 'Credit Purchase'}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-green-600">+${ref.commission_amount.toFixed(2)}</p>
                        <Badge variant="outline" className={ref.commission_status === 'paid' ? 'border-green-500 text-green-600' : ''}>
                          {ref.commission_status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Public collaborator page / application form
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => {
          // Navigate based on auth status
          if (!user) {
            navigate('/');
          } else if (window.history.length > 2 && document.referrer) {
            navigate(-1);
          } else {
            navigate('/dashboard');
          }
        }} className="mb-6" data-testid="collaborators-back-button">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-purple-100 text-purple-700">
            <Sparkles className="w-3 h-3 mr-1" /> Collaborator Program
          </Badge>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Earn Money Sharing SlideCon
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Join our collaborator program and earn up to 25% commission on every sale through your personal discount code.
          </p>
        </div>

        {/* Pending/Rejected Status */}
        {collaboratorStatus?.is_collaborator && collaboratorStatus?.collaborator?.status !== 'approved' && (
          <Card className="mb-8 border-2 border-yellow-400">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                {getStatusBadge(collaboratorStatus.collaborator.status)}
                <div>
                  {collaboratorStatus.collaborator.status === 'pending' && (
                    <p>Your application is being reviewed. We&apos;ll get back to you within 48 hours.</p>
                  )}
                  {collaboratorStatus.collaborator.status === 'rejected' && (
                    <p>Your application was rejected: {collaboratorStatus.collaborator.rejection_reason}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tier Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {tiers.map((tier) => (
            <Card key={tier.tier} className={`relative ${tier.tier === 'pro' ? 'border-2 border-amber-500' : ''}`}>
              {tier.tier === 'pro' && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500">
                  Higher Earnings
                </Badge>
              )}
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {tier.tier === 'pro' ? (
                    <Award className="w-6 h-6 text-amber-500" />
                  ) : (
                    <Target className="w-6 h-6 text-blue-500" />
                  )}
                  {tier.tier === 'pro' ? 'Pro Tier' : 'Starter Tier'}
                </CardTitle>
                <CardDescription>{tier.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <span className="text-5xl font-bold text-gray-900 dark:text-white">{tier.commission}%</span>
                  <p className="text-gray-500">commission per sale</p>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {tier.min_followers.toLocaleString()}+ followers required
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Personal discount code
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Real-time earnings dashboard
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Monthly payouts via PayPal
                  </li>
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* How It Works */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle>How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-purple-600">1</span>
                </div>
                <h3 className="font-semibold mb-2">Apply</h3>
                <p className="text-sm text-gray-500">Submit your application with your social media profiles</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-purple-600">2</span>
                </div>
                <h3 className="font-semibold mb-2">Get Your Code</h3>
                <p className="text-sm text-gray-500">Once approved, receive your personal discount code</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-purple-600">3</span>
                </div>
                <h3 className="font-semibold mb-2">Earn</h3>
                <p className="text-sm text-gray-500">Get commission every time someone uses your code</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center">
          {!token ? (
            <Button size="lg" onClick={() => navigate('/login?redirect=/collaborators')}>
              Sign In to Apply
            </Button>
          ) : collaboratorStatus?.is_collaborator ? (
            <p className="text-gray-500">You have already applied to the program.</p>
          ) : (
            <Button size="lg" className="bg-purple-600 hover:bg-purple-700" onClick={() => setShowApplyDialog(true)}>
              <Gift className="w-5 h-5 mr-2" /> Apply Now
            </Button>
          )}
        </div>

        {/* Application Dialog */}
        <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Apply to Collaborator Program</DialogTitle>
              <DialogDescription>
                Fill out the form below to apply. We review applications within 48 hours.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <Label>Display Name *</Label>
                <Input
                  placeholder="Your brand or creator name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                />
              </div>

              <div>
                <Label>Bio</Label>
                <Textarea
                  placeholder="Tell us about yourself and your audience..."
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                />
              </div>

              <div>
                <Label>Total Follower Count *</Label>
                <Input
                  type="number"
                  placeholder="Combined followers across all platforms"
                  value={formData.follower_count}
                  onChange={(e) => setFormData({ ...formData, follower_count: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 1,000 followers required</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-1"><Instagram className="w-4 h-4" /> Instagram</Label>
                  <Input
                    placeholder="@username"
                    value={formData.social_links.instagram}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      social_links: { ...formData.social_links, instagram: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Youtube className="w-4 h-4" /> YouTube</Label>
                  <Input
                    placeholder="Channel URL"
                    value={formData.social_links.youtube}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      social_links: { ...formData.social_links, youtube: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Twitter className="w-4 h-4" /> Twitter/X</Label>
                  <Input
                    placeholder="@username"
                    value={formData.social_links.twitter}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      social_links: { ...formData.social_links, twitter: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Globe className="w-4 h-4" /> Website</Label>
                  <Input
                    placeholder="https://..."
                    value={formData.social_links.website}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      social_links: { ...formData.social_links, website: e.target.value }
                    })}
                  />
                </div>
              </div>

              <div>
                <Label>PayPal Email *</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.payment_email}
                  onChange={(e) => setFormData({ ...formData, payment_email: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Where we&apos;ll send your earnings</p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApplyDialog(false)}>Cancel</Button>
              <Button onClick={handleApply} disabled={applying}>
                {applying ? 'Submitting...' : 'Submit Application'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
